import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { InputField } from "@/components/InputField/InputField";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";
import { useSnackBar } from "@/utils/useSnackbar";

import {
	useGetDynamicsLeadConfigQuery,
	useGetDynamicsOwnersQuery,
	useSaveDynamicsLeadConfigMutation,
} from "@/pages/Private/redux/dynamics/dynamics.api";

import { UpdateMsdynamicsLeadConfig } from "../schema/dynamics";

interface LeadConfigDraft {
	leadTopicTemplate: string;
	defaultOwnerId: string | null;
	estimatedValue: number | null;
	expectedCloseDateOffset: number | null;
}

const EMPTY_DRAFT: LeadConfigDraft = {
	leadTopicTemplate: "",
	defaultOwnerId: null,
	estimatedValue: null,
	expectedCloseDateOffset: null,
};

/** `PUT lead-config` patches — an unset value is omitted, never sent as `null`. */
const buildPayload = (draft: LeadConfigDraft): UpdateMsdynamicsLeadConfig => {
	const payload: UpdateMsdynamicsLeadConfig = {};

	if (draft.leadTopicTemplate.trim()) {
		payload.leadTopicTemplate = draft.leadTopicTemplate.trim();
	}

	if (draft.defaultOwnerId) {
		payload.defaultOwnerId = draft.defaultOwnerId;
	}

	if (draft.estimatedValue !== null) {
		payload.estimatedValue = draft.estimatedValue;
	}

	if (draft.expectedCloseDateOffset !== null) {
		payload.expectedCloseDateOffset = draft.expectedCloseDateOffset;
	}

	return payload;
};

/**
 * Lead defaults.
 *
 * Two Pipedrive fields are deliberately absent: **currency** (Dataverse takes it from the
 * `transactioncurrencyid` lookup) and **labels** (Dataverse has no lead labels — the nearest
 * equivalents, `leadqualitycode` / `leadsourcecode`, are ordinary field-mapping targets and
 * belong in the mapping modal).
 *
 * A Dataverse lead's title is `subject`, hence `leadTopicTemplate` rather than a reuse of the
 * opportunity name template — the sync falls back to `dealNameTemplate` when this is unset,
 * which the placeholder says instead of duplicating the value into the field.
 */
export const DynamicsLeadConfigTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.msdynamics.leadConfig.${key}`);

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { data: leadConfig, isLoading: isLoadingConfig } = useGetDynamicsLeadConfigQuery();
	const { data: ownersData } = useGetDynamicsOwnersQuery();
	const [saveLeadConfig, { isLoading: isSaving }] = useSaveDynamicsLeadConfigMutation();

	const [draft, setDraft] = useState<LeadConfigDraft>(EMPTY_DRAFT);

	useEffect(() => {
		if (!leadConfig) {
			return;
		}

		setDraft({
			leadTopicTemplate: leadConfig.leadTopicTemplate ?? "",
			defaultOwnerId: leadConfig.defaultOwnerId ?? null,
			estimatedValue: leadConfig.estimatedValue ?? null,
			expectedCloseDateOffset: leadConfig.expectedCloseDateOffset ?? null,
		});
	}, [leadConfig]);

	const ownerOptions: DropdownAutoCompleteItem[] = useMemo(
		() =>
			(ownersData?.owners ?? []).map((owner) => ({
				id: owner.id,
				title: owner.name ? `${owner.name} (${owner.email})` : owner.email,
			})),
		[ownersData]
	);

	const handleSave = async () => {
		try {
			await saveLeadConfig(buildPayload(draft)).unwrap();
			handleOpenSnackbar(ts("saved"));
		} catch {
			handleOpenSnackbar(ts("saveError"));
		}
	};

	if (isLoadingConfig) {
		return <LoadingBlock />;
	}

	return (
		<div className="flex flex-col w-full gap-4">
			{renderMessageSnackbar()}
			<div className="text-sm text-text-meta mb-1">{ts("description")}</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<div className="md:col-span-2">
					<InputField
						handleChange={(event) =>
							setDraft((prev) => ({ ...prev, leadTopicTemplate: event.target.value }))
						}
						information={ts("leadTopicTemplateHelp")}
						label={ts("leadTopicTemplate")}
						name="leadTopicTemplate"
						placeholder={ts("leadTopicTemplatePlaceholder")}
						value={draft.leadTopicTemplate}
					/>
				</div>

				<Dropdown
					data={ownerOptions}
					defaultValue={ownerOptions.find((option) => option.id === draft.defaultOwnerId)}
					floating={true}
					handleSelect={(value) =>
						setDraft((prev) => ({ ...prev, defaultOwnerId: value.id as string }))
					}
					label={ts("defaultOwner")}
				/>

				<InputField
					handleChange={(event) => {
						const parsed = parseFloat(event.target.value);

						setDraft((prev) => ({
							...prev,
							estimatedValue: Number.isFinite(parsed) && parsed >= 0 ? parsed : null,
						}));
					}}
					information={ts("estimatedValueHelp")}
					label={ts("estimatedValue")}
					min={0}
					name="estimatedValue"
					type="number"
					value={draft.estimatedValue ?? ""}
				/>

				<InputField
					handleChange={(event) => {
						const parsed = parseInt(event.target.value, 10);

						setDraft((prev) => ({
							...prev,
							expectedCloseDateOffset:
								Number.isFinite(parsed) && parsed >= 0 && parsed <= 3650 ? parsed : null,
						}));
					}}
					information={ts("closeDateOffsetHelp")}
					label={ts("closeDateOffsetDays")}
					max={3650}
					min={0}
					name="expectedCloseDateOffset"
					type="number"
					value={draft.expectedCloseDateOffset ?? ""}
				/>
			</div>

			<div className="flex justify-end mt-4">
				<Button
					color={ButtonColor.ACTION}
					isLoading={isSaving}
					size={ButtonSize.S}
					testId="dynamics-lead-config-save"
					title={t("basics.save")}
					type="button"
					onClick={handleSave}
				/>
			</div>
		</div>
	);
};
